<script setup lang="ts">
import { computed } from "vue";
import { CheckCircle2, ChevronDown } from "lucide-vue-next";

type RepairItem = {
  key: string;
  title: string;
  description: string;
  acceptance: string;
  completed: boolean;
};

const props = defineProps<{
  items?: RepairItem[];
  editable?: boolean;
  busy?: boolean;
}>();
const emit = defineEmits<{
  toggle: [key: string, completed: boolean];
}>();
const doneCount = computed(
  () => (props.items || []).filter((item) => item.completed).length,
);
const percent = computed(() =>
  props.items?.length ? Math.round((doneCount.value / props.items.length) * 100) : 0,
);
</script>

<template>
  <section v-if="items?.length" class="repair-list">
    <header>
      <div><h3>执行处理清单</h3><small>{{ doneCount }} / {{ items.length }} 已完成</small></div>
      <b>{{ percent }}%</b>
    </header>
    <div class="repair-progress"><i :style="{ width: `${percent}%` }" /></div>
    <details v-for="item in items" :key="item.key" :class="{ done: item.completed }">
      <summary>
        <label @click.stop>
          <input
            type="checkbox"
            :checked="item.completed"
            :disabled="!editable || busy"
            @change="emit('toggle', item.key, ($event.target as HTMLInputElement).checked)"
          />
          <CheckCircle2 v-if="item.completed" :size="17" />
          <span>{{ item.title }}</span>
        </label>
        <ChevronDown :size="16" />
      </summary>
      <div class="repair-detail">
        <p>{{ item.description }}</p>
        <small><b>完成标准</b>{{ item.acceptance }}</small>
      </div>
    </details>
  </section>
</template>

<style scoped>
.repair-list{padding:0!important;border:1px solid var(--line)!important;background:#fff}.repair-list>header{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:13px 14px}.repair-list h3,.repair-list small{margin:0}.repair-list small{display:block;margin-top:3px;color:var(--muted);font-weight:400}.repair-list>header>b{color:var(--brand-dark);font-size:13px}.repair-progress{height:3px;background:#e9eeee}.repair-progress i{display:block;height:100%;background:var(--success);transition:width .2s ease}.repair-list details{border-top:1px solid var(--line)}.repair-list summary{display:grid;grid-template-columns:minmax(0,1fr) 20px;align-items:center;gap:8px;min-height:48px;padding:7px 12px;cursor:pointer;list-style:none}.repair-list summary::-webkit-details-marker{display:none}.repair-list summary label{display:grid;grid-template-columns:22px 18px minmax(0,1fr);align-items:center;gap:7px;min-width:0;cursor:pointer}.repair-list summary input{width:18px;height:18px;margin:0;accent-color:var(--success)}.repair-list summary svg{color:var(--success)}.repair-list summary>svg{color:var(--muted);transition:transform .2s ease}.repair-list details[open] summary>svg{transform:rotate(180deg)}.repair-list details.done summary span{color:var(--muted);text-decoration:line-through}.repair-detail{padding:0 14px 13px 59px}.repair-detail p{margin:0 0 7px;line-height:1.6}.repair-detail small{display:grid;grid-template-columns:auto minmax(0,1fr);gap:8px;padding:8px;background:#f5f8f8;color:var(--muted);line-height:1.5}.repair-detail small b{color:var(--ink)}
@media(max-width:700px){.repair-detail{padding-left:14px}.repair-list summary{min-height:54px}}
@media(prefers-reduced-motion:reduce){.repair-progress i,.repair-list summary>svg{transition:none}}
</style>
